import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ArrowUpRight, Cpu, Wrench, Code, Activity, Settings, Zap } from "lucide-react";
import { Reveal, stagger, fadeUp } from "@/components/common/Reveal";
import Counter from "@/components/common/Counter";
import { COMPANY, STATS, SERVICES, INDUSTRIES, CLIENTS } from "@/data/content";

const ICONS = { cpu: Cpu, wrench: Wrench, code: Code, activity: Activity, settings: Settings, zap: Zap };
const heroImg = "/machines/wmw-union-hmc.png";

const HeroLine = ({ children, delay }) => (
  <span className="mask-line" style={{ overflow: "visible" }}>
    <motion.span initial={{ y: "110%", opacity: 0 }} animate={{ y: "0%", opacity: 1 }} transition={{ duration: 0.9, delay, ease: [0.2, 0.8, 0.2, 1] }} className="block">
      {children}
    </motion.span>
  </span>
);

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const base = 1.5;

  return (
    <div>
      {/* ===== HERO ===== */}
      <section ref={heroRef} className="relative overflow-hidden bg-white" data-testid="hero">
        <motion.div className="absolute top-0 left-0 right-0 h-[400px] md:h-[520px] -z-10 overflow-hidden" style={{ y }}>
          <img src={heroImg} alt="Precision CNC machining" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,10,10,0.35) 0%, rgba(10,10,10,0.15) 55%, rgba(255,255,255,1) 100%)" }} />
        </motion.div>
        <div className="absolute inset-0 grid-lines -z-10 opacity-40" />

        <div className="relative max-w-[1200px] mx-auto px-6 md:px-10 w-full mt-auto pb-12 md:pb-16 pt-24 md:pt-28">
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: base + 0.1, duration: 0.8 }} className="eyebrow" data-testid="hero-eyebrow">
            Machine Retrofitters &amp; Reconditioners
          </motion.span>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl md:text-6xl lg:text-[68px] leading-[1.02] text-slate-900" data-testid="hero-headline">
            <HeroLine delay={base}>Your Problem.</HeroLine>
            <HeroLine delay={base + 0.12}><span className="text-[#0055FF]">Our Solution.</span></HeroLine>
          </h1>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: base + 0.4, duration: 0.7 }}
            className="mt-6 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <p className="text-dim text-base max-w-xl leading-relaxed">
              Two decades of engineering craft — reconditioning, retrofitting and automating machine tools across India with FANUC and Siemens controls.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/machines" className="btn-primary !text-sm" data-testid="hero-machines-btn">Explore Machines <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/quote" className="btn-ghost !text-sm" data-testid="hero-quote-btn">Request a Quote</Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== STATS ===== */}
      <section className="border-y border-slate-200 bg-white" data-testid="stats">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 grid grid-cols-2 lg:grid-cols-4 divide-x divide-slate-200">
          {STATS.map((s, i) => (
            <Reveal
            key={s.label}
            delay={i * 0.08}
            className="min-w-0 overflow-visible py-8 px-5"
            >
              <div className="font-display text-3xl md:text-4xl text-slate-900 whitespace-nowrap">
                <Counter
                  value={s.value}
                  suffix={s.suffix}
                />
              </div>
              <p className="mt-2 text-xs text-dim uppercase tracking-wider leading-relaxed">
                {s.label}
              </p>
            </Reveal>
          ))}
          </div>
        </section>

      {/* ===== INTRO ===== */}
      <section className="py-10 md:py-14 relative" data-testid="intro">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4">
            <Reveal><span className="eyebrow">01 — Who We Are</span></Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal><h2 className="text-2xl md:text-4xl leading-[1.1] max-w-3xl text-slate-900">A trusted engineering partner for India's precision manufacturers.</h2></Reveal>
            <Reveal delay={0.1}><p className="mt-6 text-dim text-base leading-relaxed max-w-2xl">For over two decades, {COMPANY.legal} has restored, upgraded and automated CNC machine tools — combining old-world mechanical craft with the latest FANUC and Siemens control technology.</p></Reveal>
            <Reveal delay={0.2}>
              <Link to="/about" className="mt-6 inline-flex items-center gap-2 text-sm text-slate-900 group">
                <span className="border-b border-[#0055FF] pb-1">Discover our story</span>
                <ArrowUpRight className="w-4 h-4 text-[#0055FF] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===== SERVICES ===== */}
      <section className="py-10 md:py-14 border-t border-slate-200" data-testid="services-overview">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
            <div>
              <span className="eyebrow">02 — What We Do</span>
              <h2 className="mt-4 text-2xl md:text-4xl text-slate-900">Engineering Services</h2>
            </div>
            <Link to="/services" className="btn-ghost self-start md:self-auto !py-2.5 !px-5 !text-sm">All Services <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICES.slice(0, 6).map((s) => {
              const Icon = ICONS[s.icon] || Cpu;
              return (
                <motion.div key={s.slug} variants={fadeUp}>
                  <Link to="/services" className="group block h-full bg-white border border-[#0055FF]/30 rounded-2xl p-6 card-hover" data-testid={`service-card-${s.slug}`}>
                    <div className="w-10 h-10 rounded-lg bg-[#0055FF]/10 flex items-center justify-center border border-[#0055FF]/25">
                      <Icon className="w-5 h-5 text-[#0055FF]" />
                    </div>
                    <h3 className="mt-4 text-lg text-slate-900">{s.title}</h3>
                    <p className="mt-2 text-dim text-sm leading-relaxed">{s.short}</p>
                    <ArrowUpRight className="mt-4 w-4 h-4 text-slate-400 group-hover:text-[#0055FF] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
                  </Link>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ===== CONTROLLER BRAND LOGOS (compact, no big photos) ===== */}
      <section className="py-10 md:py-14 border-t border-slate-200" data-testid="controllers-home">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="mb-8">
            <span className="eyebrow">03 — Control Partners</span>
            <h2 className="mt-4 text-2xl md:text-4xl text-slate-900">We work with FANUC &amp; Siemens</h2>
            <p className="mt-3 text-dim text-sm max-w-xl">Our machines use FANUC and Siemens CNC controllers — with assistance and support provided directly from FANUC and Siemens support teams.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Reveal>
              <Link to="/services" className="group flex items-center gap-6 rounded-2xl bg-white border border-slate-200 p-6 card-hover" data-testid="brand-fanuc">
                <div className="w-24 h-24 shrink-0 rounded-xl bg-[#111] flex items-center justify-center">
                  <span className="font-display text-2xl font-black tracking-tight" style={{ color: "#FFCC00" }}>FANUC</span>
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg text-slate-900">FANUC CNC Systems</h3>
                  <p className="text-sm text-dim mt-1">The world standard in CNC control — 0i, 31i, 32i and αi/βi drives.</p>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-[#0055FF] text-sm font-medium">Explore <ArrowRight className="w-4 h-4" /></span>
                </div>
              </Link>
            </Reveal>
            <Reveal delay={0.1}>
              <Link to="/services" className="group flex items-center gap-6 rounded-2xl bg-white border border-slate-200 p-6 card-hover" data-testid="brand-siemens">
                <div className="w-24 h-24 shrink-0 rounded-xl bg-white border border-slate-200 flex items-center justify-center">
                  <span className="font-display text-xl font-bold tracking-tight" style={{ color: "#009999" }}>SIEMENS</span>
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg text-slate-900">SINUMERIK Systems</h3>
                  <p className="text-sm text-dim mt-1">808D, 828D, 840D sl and SINAMICS drives — intelligent machining.</p>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-[#0055FF] text-sm font-medium">Explore <ArrowRight className="w-4 h-4" /></span>
                </div>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===== INDUSTRIES / CLIENTS ===== */}
      <section className="py-10 md:py-14 border-t border-slate-200" data-testid="industries-home">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <span className="eyebrow">04 — Industries Served</span>
          <h2 className="mt-4 text-2xl md:text-4xl mb-8 text-slate-900">Trusted across sectors</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 rounded-2xl overflow-hidden border border-slate-200">
            {INDUSTRIES.map((ind, i) => (
              <Reveal key={ind.name} delay={i * 0.04} className="bg-white p-6 hover:bg-slate-50 transition-colors">
                <h3 className="text-lg text-slate-900">{ind.name}</h3>
                <p className="mt-1 text-dim text-sm">{ind.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CLIENT MARQUEE (black text) ===== */}
      <section className="py-10 border-y border-slate-200 bg-white" data-testid="clients-marquee">
        <p className="text-center text-[11px] uppercase tracking-[0.3em] text-slate-500 mb-6">Trusted by industry leaders</p>
        <Marquee gradient gradientColor="#FFFFFF" gradientWidth={100} speed={40}>
          {CLIENTS.map((c) => (
            <span key={c} className="font-display text-xl md:text-2xl text-slate-900 hover:text-[#0055FF] transition-colors mx-8">{c}</span>
          ))}
        </Marquee>
      </section>

      {/* ===== CONTROL PARTNERS strip ===== */}
      <section className="py-10 border-t border-slate-200 bg-slate-50" data-testid="certifications-home">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 text-center">
          <p className="text-[11px] uppercase tracking-[0.3em] text-slate-500">CNC Control Partners</p>
          <p className="mt-3 text-sm md:text-base text-slate-800 max-w-3xl mx-auto leading-relaxed">
            We work with <span className="font-semibold text-slate-900">FANUC</span> and <span className="font-semibold text-slate-900">Siemens</span> CNC controllers, and provide assistance and support directly from the FANUC and Siemens support teams.
          </p>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="relative py-10 md:py-14 overflow-hidden bg-white" data-testid="cta-home">
        <div className="absolute inset-0 -z-10" style={{ background: "radial-gradient(circle at 50% 120%, rgba(0,85,255,0.12), transparent 60%)" }} />
        <div className="absolute inset-0 grid-lines -z-10 opacity-40" />
        <div className="max-w-[900px] mx-auto px-6 text-center">
          <Reveal><h2 className="text-3xl md:text-5xl leading-[1.05] text-slate-900">Let's engineer your next<br /><span className="text-[#0055FF]">precision advantage.</span></h2></Reveal>
          <Reveal delay={0.1}><p className="mt-4 text-dim text-base max-w-xl mx-auto">Talk to our engineers about retrofitting, reconditioning, or acquiring a fully-tested machine.</p></Reveal>
          <Reveal delay={0.2}>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link to="/contact" className="btn-primary !text-sm" data-testid="cta-contact-btn">Contact Us <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/enquiry" className="btn-ghost !text-sm" data-testid="cta-enquiry-btn">Send an Enquiry</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
